/**
 * Character Sheet — play view. MERGED only. Players never see Sheet A/B
 * during play; the split is only on the level-up page.
 *
 * First live write path: HP edits ride the WebSocket $default dispatcher.
 * The page fetches the character roster over the HTTP read path on mount,
 * picks the first character (multi-character pickers are out of scope for
 * this slice), then opens a campaign socket and listens for runtime patches.
 *
 * Mutations are optimistic: the local state moves immediately and the
 * pending version is tracked. The server's echoed patch (or rejection)
 * confirms or replaces it. Rejections reconcile the local state to the
 * server version — the simplest correct strategy until the audit log
 * grows a "retry with replay" path.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CharacterBundle,
  DEFAULT_SESSION_ID,
  getCharacters,
} from '../lib/sessionApi';
import { CampaignSocket } from '../lib/campaignSocket';
import { reportFrontendError } from '../lib/errorCapture';
import {
  ConditionsUpdateRequest,
  HitDiceUpdateRequest,
  HpUpdateRequest,
  InspirationUpdateRequest,
  InventoryUpdateRequest,
  ResourcesUpdateRequest,
  RuntimeActions,
  RuntimePatchPayload,
  RuntimeRejectedPayload,
  RuntimeServerTypes,
  SlotsUpdateRequest,
  SpellSlotsState,
} from '../lib/runtimeProtocol';
import {
  SessionActions,
  SessionServerTypes,
  LogEntryPayload,
  RollRequest,
} from '../lib/sessionProtocol';
import { rollDice, formatRoll } from '../lib/diceEngine';
import { ActiveCondition, InventoryItem, ResourceCounter } from '../lib/runtime';

// Canonical 5e conditions. The runtime type allows any string so campaign
// homebrew can extend this, but the picker UI only surfaces the standard set
// — typing an unknown chip inline isn't wired yet.
const STANDARD_CONDITIONS: readonly string[] = [
  'blinded',
  'charmed',
  'deafened',
  'frightened',
  'grappled',
  'incapacitated',
  'invisible',
  'paralyzed',
  'petrified',
  'poisoned',
  'prone',
  'restrained',
  'stunned',
  'unconscious',
  'exhaustion',
] as const;

// Sheet doesn't render a map but the WS subscribe needs one to bind the
// connection row's SessionId. Reuse the same default the VTT page uses so
// both pages share a session view.
const DEFAULT_MAP_ID = 'local-dev';

interface SheetState {
  bundle: CharacterBundle;
  // Local mirror of the runtime fields the sheet actually drives. Kept
  // separate from the bundle so optimistic updates don't mutate the
  // server-derived snapshot.
  currentHp: number;
  tempHp: number;
  maxHp: number;
  // Per-sheet expended-slot map. Keys are sheet ids ("sheet-a", "sheet-b").
  // Empty object on a freshly seeded character.
  spellSlots: Record<string, SpellSlotsState>;
  // Hit dice spent counter. Max = combined level (Sheet A + Sheet B always
  // level together). Derived client-side for the UI cap; the server
  // trusts the absolute value in the same way it does HP.
  hitDiceSpent: number;
  maxHitDice: number;
  // Binary 5e inspiration flag — you have it or you don't, no stacks.
  inspiration: boolean;
  // Active conditions list. Full-replace on every edit; exhaustion carries
  // a level 1..6 on the row, all other 5e conditions are binary.
  conditions: ActiveCondition[];
  // Class-resource counters — Ki, Channel Divinity, Sorcery Points, etc.
  // Max lives on the ledger (not wired here yet); this just tracks spent.
  resources: ResourceCounter[];
  // Inventory items with equipped/stowed flag. Full-replace on every edit.
  inventory: InventoryItem[];
  version: number;
}

/**
 * Pull combined level out of the (loosely typed) profile so we can compute
 * the hit-dice cap. Both sheets always level together, so combined =
 * sheetA.level * 2. Default 2 so a half-seeded bundle doesn't render a
 * zero-cap counter that looks broken.
 */
function readCombinedLevel(bundle: CharacterBundle): number {
  const profile = bundle.profile as
    | { sheetA?: { level?: number } }
    | null;
  const levelA = profile?.sheetA?.level ?? 1;
  return Math.max(1, levelA * 2);
}

function readMaxHp(bundle: CharacterBundle): number {
  // Placeholder until the merged ledger derives true max HP. The seed
  // writes a placeholderMaxHp into the runtime.currentHp on first save,
  // so on a freshly seeded character we can use that as max. After any
  // mutation the runtime.currentHp drifts from max — fall back to a
  // sensible derivation when that happens.
  const profile = bundle.profile as
    | { sheetA?: { hitDieTotal?: number }; sheetB?: { hitDieTotal?: number } }
    | null;
  if (profile?.sheetA?.hitDieTotal && profile?.sheetB?.hitDieTotal) {
    return profile.sheetA.hitDieTotal + profile.sheetB.hitDieTotal + 1;
  }
  return Math.max(1, bundle.runtime?.currentHp ?? 1);
}

function bundleToState(bundle: CharacterBundle): SheetState {
  const r = bundle.runtime ?? {};
  // Runtime is typed as Partial<CharacterRuntime> in sessionApi but the seed
  // writes an empty object for a fresh character — coerce defensively.
  const slots = (r as { spellSlots?: Record<string, SpellSlotsState> }).spellSlots ?? {};
  return {
    bundle,
    currentHp:    r.currentHp ?? 0,
    tempHp:       r.tempHp ?? 0,
    maxHp:        readMaxHp(bundle),
    spellSlots:   slots,
    hitDiceSpent: r.hitDiceSpent ?? 0,
    maxHitDice:   readCombinedLevel(bundle),
    inspiration:  r.inspiration ?? false,
    conditions:   r.conditions ?? [],
    resources:    r.resources ?? [],
    inventory:    r.inventory ?? [],
    version:      r.version ?? 0,
  };
}

export default function Sheet() {
  const [state, setState] = useState<SheetState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  // Draft for the "add resource" input. Local-only; never goes to the wire
  // until the user hits Add.
  const [newResourceId, setNewResourceId] = useState('');
  const [newItemName, setNewItemName] = useState('');
  // Dice roller state.
  const [diceExpr, setDiceExpr] = useState('');
  const [lastRoll, setLastRoll] = useState<{ expression: string; results: number[]; total: number; modifier: number } | null>(null);
  const [diceError, setDiceError] = useState<string | null>(null);
  // Session log — recent entries, newest-first. Capped at 50 to avoid
  // unbounded memory growth during long sessions.
  const [logEntries, setLogEntries] = useState<LogEntryPayload[]>([]);
  const socketRef = useRef<CampaignSocket | null>(null);
  // Tracks the version we expect the next server patch to carry. Pending
  // mutations bump this optimistically; rejections snap it back.
  const pendingVersionRef = useRef<number>(0);

  // Load the roster + open the socket once on mount.
  useEffect(() => {
    let cancelled = false;
    let cs: CampaignSocket | null = null;

    (async () => {
      try {
        const list = await getCharacters(DEFAULT_SESSION_ID);
        if (cancelled) return;
        const first = list.characters[0];
        if (!first) {
          setError('No characters in this session yet.');
          setLoading(false);
          return;
        }
        const initial = bundleToState(first);
        setState(initial);
        pendingVersionRef.current = initial.version;
        setLoading(false);

        cs = await CampaignSocket.connect({
          sessionId: DEFAULT_SESSION_ID,
          mapId: DEFAULT_MAP_ID,
        });
        if (cancelled) {
          cs.close();
          return;
        }
        socketRef.current = cs;

        cs.on<RuntimePatchPayload>(RuntimeServerTypes.RuntimePatch, (payload) => {
          setState((prev) => {
            if (!prev || prev.bundle.id !== payload.characterId) return prev;
            // Only apply patches that move us forward — out-of-order echoes
            // (which shouldn't happen on a single connection but are cheap
            // to guard against) are ignored.
            if (payload.version <= prev.version) return prev;
            pendingVersionRef.current = payload.version;
            return {
              ...prev,
              version:      payload.version,
              currentHp:    payload.currentHp    ?? prev.currentHp,
              tempHp:       payload.tempHp       ?? prev.tempHp,
              spellSlots:   payload.spellSlots   ?? prev.spellSlots,
              hitDiceSpent: payload.hitDiceSpent ?? prev.hitDiceSpent,
              inspiration:  payload.inspiration  ?? prev.inspiration,
              conditions:   payload.conditions   ?? prev.conditions,
              resources:    payload.resources    ?? prev.resources,
              inventory:    payload.inventory    ?? prev.inventory,
            };
          });
          setConflict(null);
        });

        cs.on<RuntimeRejectedPayload>(RuntimeServerTypes.RuntimeRejected, (payload) => {
          setState((prev) => {
            if (!prev || prev.bundle.id !== payload.characterId) return prev;
            pendingVersionRef.current = payload.version;
            return {
              ...prev,
              version:      payload.version,
              currentHp:    payload.currentHp    ?? prev.currentHp,
              tempHp:       payload.tempHp       ?? prev.tempHp,
              spellSlots:   payload.spellSlots   ?? prev.spellSlots,
              hitDiceSpent: payload.hitDiceSpent ?? prev.hitDiceSpent,
              inspiration:  payload.inspiration  ?? prev.inspiration,
              conditions:   payload.conditions   ?? prev.conditions,
              resources:    payload.resources    ?? prev.resources,
              inventory:    payload.inventory    ?? prev.inventory,
            };
          });
          setConflict(`Rejected: ${payload.reason}. Reconciled to v${payload.version}.`);
        });

        // Session log: prepend new entries from the WS broadcast, cap at 50.
        cs.on<LogEntryPayload>(SessionServerTypes.LogEntry, (payload) => {
          setLogEntries((prev) => [payload, ...prev].slice(0, 50));
        });
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to open sheet');
          setLoading(false);
        }
        // Caught fetch/socket failures never reach window.onerror, so the
        // global capture hook can't see them. Push them through manually
        // so the live error logger has visibility.
        reportFrontendError(err, { source: 'page-sheet', sessionId: DEFAULT_SESSION_ID });
      }
    })();

    return () => {
      cancelled = true;
      cs?.close();
      socketRef.current = null;
    };
  }, []);

  // Slot mutations ship the FULL new map. The client computes it locally
  // (apply the intended delta on top of the current snapshot), the server
  // overwrites under a version check, and the echoed patch confirms.
  // See SlotsUpdateRequest for why we don't ship per-slot deltas.
  const sendSlotsUpdate = useCallback(
    (nextSlots: Record<string, SpellSlotsState>, reason?: string) => {
      const cs = socketRef.current;
      const cur = state;
      if (!cs || !cur) return;
      const expected = pendingVersionRef.current;
      const req: SlotsUpdateRequest = {
        action: RuntimeActions.SlotsUpdate,
        characterId: cur.bundle.id,
        expectedVersion: expected,
        spellSlots: nextSlots,
        reason,
      };
      cs.send(req);
      pendingVersionRef.current = expected + 1;
      setState((prev) =>
        prev
          ? {
              ...prev,
              spellSlots: nextSlots,
              version:    expected + 1,
            }
          : prev,
      );
    },
    [state],
  );

  const sendHpUpdate = useCallback(
    (nextCurrentHp?: number, nextTempHp?: number, reason?: string) => {
      const cs = socketRef.current;
      const cur = state;
      if (!cs || !cur) return;
      const expected = pendingVersionRef.current;
      const req: HpUpdateRequest = {
        action: RuntimeActions.HpUpdate,
        characterId: cur.bundle.id,
        expectedVersion: expected,
        currentHp: nextCurrentHp,
        tempHp:    nextTempHp,
        reason,
      };
      cs.send(req);
      // Optimistic local apply + optimistic version bump. The server's echo
      // confirms; a rejection snaps both back.
      pendingVersionRef.current = expected + 1;
      setState((prev) =>
        prev
          ? {
              ...prev,
              currentHp: nextCurrentHp ?? prev.currentHp,
              tempHp:    nextTempHp ?? prev.tempHp,
              version:   expected + 1,
            }
          : prev,
      );
    },
    [state],
  );

  // Same pattern as sendHpUpdate / sendSlotsUpdate: optimistic local
  // apply + expected-version bump, echoed runtime/patch confirms, reject
  // snaps back. Ship absolute values, not deltas, so the server stays
  // idempotent and concurrent writes can't double-apply.
  const sendHitDiceUpdate = useCallback(
    (nextSpent: number, reason?: string) => {
      const cs = socketRef.current;
      const cur = state;
      if (!cs || !cur) return;
      const expected = pendingVersionRef.current;
      const req: HitDiceUpdateRequest = {
        action: RuntimeActions.HitDiceUpdate,
        characterId: cur.bundle.id,
        expectedVersion: expected,
        hitDiceSpent: nextSpent,
        reason,
      };
      cs.send(req);
      pendingVersionRef.current = expected + 1;
      setState((prev) =>
        prev
          ? { ...prev, hitDiceSpent: nextSpent, version: expected + 1 }
          : prev,
      );
    },
    [state],
  );

  const sendInspirationUpdate = useCallback(
    (nextInspiration: boolean, reason?: string) => {
      const cs = socketRef.current;
      const cur = state;
      if (!cs || !cur) return;
      const expected = pendingVersionRef.current;
      const req: InspirationUpdateRequest = {
        action: RuntimeActions.InspirationUpdate,
        characterId: cur.bundle.id,
        expectedVersion: expected,
        inspiration: nextInspiration,
        reason,
      };
      cs.send(req);
      pendingVersionRef.current = expected + 1;
      setState((prev) =>
        prev
          ? { ...prev, inspiration: nextInspiration, version: expected + 1 }
          : prev,
      );
    },
    [state],
  );

  // Conditions always ship as the full new list — the client computes the
  // next state locally (add/remove/bump-exhaustion) and the server
  // overwrites under a version check. Same full-replace rationale as slots.
  const sendConditionsUpdate = useCallback(
    (nextConditions: ActiveCondition[], reason?: string) => {
      const cs = socketRef.current;
      const cur = state;
      if (!cs || !cur) return;
      const expected = pendingVersionRef.current;
      const req: ConditionsUpdateRequest = {
        action: RuntimeActions.ConditionsUpdate,
        characterId: cur.bundle.id,
        expectedVersion: expected,
        conditions: nextConditions,
        reason,
      };
      cs.send(req);
      pendingVersionRef.current = expected + 1;
      setState((prev) =>
        prev
          ? { ...prev, conditions: nextConditions, version: expected + 1 }
          : prev,
      );
    },
    [state],
  );

  // Class-resource counters. Full-list replace with the same optimistic
  // apply / expected-version bump / echo-as-ack dance. Max lives on the
  // ledger, not here — this view only drives the spent values.
  const sendResourcesUpdate = useCallback(
    (nextResources: ResourceCounter[], reason?: string) => {
      const cs = socketRef.current;
      const cur = state;
      if (!cs || !cur) return;
      const expected = pendingVersionRef.current;
      const req: ResourcesUpdateRequest = {
        action: RuntimeActions.ResourcesUpdate,
        characterId: cur.bundle.id,
        expectedVersion: expected,
        resources: nextResources,
        reason,
      };
      cs.send(req);
      pendingVersionRef.current = expected + 1;
      setState((prev) =>
        prev
          ? { ...prev, resources: nextResources, version: expected + 1 }
          : prev,
      );
    },
    [state],
  );

  // Inventory mutations. Full-list replace with the same optimistic apply /
  // expected-version bump / echo-as-ack dance. Toggle equipped/stowed, adjust
  // quantity, add/remove items.
  const sendInventoryUpdate = useCallback(
    (nextInventory: InventoryItem[], reason?: string) => {
      const cs = socketRef.current;
      const cur = state;
      if (!cs || !cur) return;
      const expected = pendingVersionRef.current;
      const req: InventoryUpdateRequest = {
        action: RuntimeActions.InventoryUpdate,
        characterId: cur.bundle.id,
        expectedVersion: expected,
        inventory: nextInventory,
        reason,
      };
      cs.send(req);
      pendingVersionRef.current = expected + 1;
      setState((prev) =>
        prev
          ? { ...prev, inventory: nextInventory, version: expected + 1 }
          : prev,
      );
    },
    [state],
  );

  // Roll dice: parse + roll client-side, send result to server for audit + broadcast.
  const doRoll = useCallback(
    (expression: string, reason?: string) => {
      const cs = socketRef.current;
      if (!cs) return;
      try {
        const result = rollDice(expression);
        setLastRoll(result);
        setDiceError(null);
        const req: RollRequest = {
          action: SessionActions.Roll,
          expression: result.expression,
          results: result.results,
          total: result.total,
          reason,
        };
        cs.send(req);
      } catch (err) {
        setDiceError(err instanceof Error ? err.message : 'Invalid expression');
        setLastRoll(null);
      }
    },
    [],
  );

  // Quick-roll helper for the common-die buttons.
  const quickRoll = useCallback(
    (faces: number) => doRoll(`1d${faces}`),
    [doRoll],
  );

  if (loading) return <p>Loading sheet…</p>;
  if (error) return <p style={{ color: 'var(--danger)' }}>Error: {error}</p>;
  if (!state) return <p>No character.</p>;

  const {
    bundle,
    currentHp,
    tempHp,
    maxHp,
    spellSlots,
    hitDiceSpent,
    maxHitDice,
    inspiration,
    conditions,
    resources,
    inventory,
    version,
  } = state;
  // Lookup by id so add/remove/bump can rebuild the full list in one shot.
  const conditionById = new Map(conditions.map((c) => [c.id, c]));
  const hasCondition = (id: string) => conditionById.has(id);

  // Toggle or remove a binary condition. Exhaustion is handled separately
  // because it carries a level and doesn't round-trip through a plain toggle.
  const toggleBinary = (id: string) => {
    if (hasCondition(id)) {
      sendConditionsUpdate(conditions.filter((c) => c.id !== id), `-${id}`);
    } else {
      sendConditionsUpdate([...conditions, { id }], `+${id}`);
    }
  };

  // Exhaustion ranges 1..6 (5e 2014). Level 0 removes the row entirely.
  const setExhaustion = (level: number) => {
    const without = conditions.filter((c) => c.id !== 'exhaustion');
    if (level <= 0) {
      sendConditionsUpdate(without, 'exhaustion cleared');
      return;
    }
    const clamped = Math.min(6, Math.max(1, level));
    sendConditionsUpdate(
      [...without, { id: 'exhaustion', level: clamped }],
      `exhaustion ${clamped}`,
    );
  };
  const exhaustion = conditionById.get('exhaustion');
  const exhaustionLevel = exhaustion?.level ?? 0;

  // Adjust one resource counter's spent value by a delta and ship the full
  // new list. Clamp at zero — negative spent is meaningless. We can't clamp
  // the upper bound until the ledger wires max into the play view.
  const adjustResource = (id: string, delta: number) => {
    const next = resources.map((r) =>
      r.id === id ? { ...r, spent: Math.max(0, r.spent + delta) } : r,
    );
    sendResourcesUpdate(next, delta > 0 ? `spent ${id}` : `recovered ${id}`);
  };
  const resetResource = (id: string) => {
    const next = resources.map((r) => (r.id === id ? { ...r, spent: 0 } : r));
    sendResourcesUpdate(next, `reset ${id}`);
  };
  const removeResource = (id: string) => {
    sendResourcesUpdate(resources.filter((r) => r.id !== id), `-${id}`);
  };
  const addResource = (id: string) => {
    const trimmed = id.trim();
    if (!trimmed) return;
    if (resources.some((r) => r.id === trimmed)) return;
    sendResourcesUpdate([...resources, { id: trimmed, spent: 0 }], `+${trimmed}`);
  };
  // Inventory helpers.
  const toggleEquipped = (itemId: string) => {
    const next = inventory.map((item) =>
      item.id === itemId ? { ...item, equipped: !item.equipped } : item,
    );
    const target = inventory.find((i) => i.id === itemId);
    const verb = target?.equipped ? 'stow' : 'equip';
    sendInventoryUpdate(next, `${verb} ${target?.name ?? itemId}`);
  };
  const adjustItemQty = (itemId: string, delta: number) => {
    const next = inventory.map((item) =>
      item.id === itemId
        ? { ...item, quantity: Math.max(1, (item.quantity ?? 1) + delta) }
        : item,
    );
    const target = inventory.find((i) => i.id === itemId);
    sendInventoryUpdate(next, `${target?.name ?? itemId} qty ${delta > 0 ? '+' : ''}${delta}`);
  };
  const removeItem = (itemId: string) => {
    const target = inventory.find((i) => i.id === itemId);
    sendInventoryUpdate(
      inventory.filter((i) => i.id !== itemId),
      `-${target?.name ?? itemId}`,
    );
  };
  const addItem = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const id = trimmed.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    if (inventory.some((i) => i.id === id)) return;
    sendInventoryUpdate(
      [...inventory, { id, name: trimmed, quantity: 1, equipped: false }],
      `+${trimmed}`,
    );
  };

  const hitDiceRemaining = Math.max(0, maxHitDice - hitDiceSpent);
  const adjustHitDice = (delta: number) => {
    const next = Math.max(0, Math.min(maxHitDice, hitDiceSpent + delta));
    if (next === hitDiceSpent) return;
    sendHitDiceUpdate(next, delta > 0 ? 'spent' : 'recovered');
  };
  const name = (bundle.profile as { name?: string } | null)?.name ?? bundle.id;

  // Damage and heal apply to currentHp first; temp HP is set explicitly via
  // its own field so a player can dial it after a Heroism cast.
  const adjustHp = (delta: number) => {
    const next = Math.max(-maxHp, Math.min(maxHp, currentHp + delta));
    sendHpUpdate(next, undefined, delta < 0 ? 'damage' : 'heal');
  };

  // Apply a slot delta to one sheet's leveled map and ship the full new map.
  // delta=+1 expends a slot, delta=-1 restores one. We clamp expended >= 0
  // here; the upper bound (max slots) lives in the ledger and isn't wired
  // into the play view yet.
  const adjustSlot = (sheetId: string, level: number, delta: number) => {
    const cur = spellSlots[sheetId] ?? { leveled: {}, pactExpended: null };
    const key = String(level);
    const next = Math.max(0, (cur.leveled[key] ?? 0) + delta);
    const nextSheet: SpellSlotsState = {
      ...cur,
      leveled: { ...cur.leveled, [key]: next },
    };
    sendSlotsUpdate({ ...spellSlots, [sheetId]: nextSheet }, delta > 0 ? 'cast' : 'recover');
  };

  const adjustPact = (sheetId: string, delta: number) => {
    const cur = spellSlots[sheetId];
    if (!cur || cur.pactExpended == null) return;
    const next = Math.max(0, cur.pactExpended + delta);
    sendSlotsUpdate(
      { ...spellSlots, [sheetId]: { ...cur, pactExpended: next } },
      delta > 0 ? 'pact-cast' : 'short-rest',
    );
  };

  const slotSheetIds = Object.keys(spellSlots);

  return (
    <div className="sheet-page">
      <h1>{name}</h1>
      <p style={{ color: 'var(--fg-muted)' }}>v{version} · {bundle.id}</p>

      <section>
        <h2>Hit Points</h2>
        <div className="numeric" style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>
          <span style={{ color: currentHp <= 0 ? 'var(--danger)' : 'var(--fg-base)' }}>
            {currentHp}
          </span>
          <span style={{ color: 'var(--fg-muted)' }}> / {maxHp}</span>
          {tempHp > 0 && (
            <span style={{ color: 'var(--sheet-b)', marginLeft: '0.75rem' }}>
              +{tempHp} temp
            </span>
          )}
        </div>

        <div className="ctrl-row">
          {[1, 5, 10].map((d) => (
            <button key={`dmg-${d}`} onClick={() => adjustHp(-d)}>
              -{d}
            </button>
          ))}
          {[1, 5, 10].map((d) => (
            <button key={`heal-${d}`} onClick={() => adjustHp(d)}>
              +{d}
            </button>
          ))}
          <button onClick={() => sendHpUpdate(maxHp, 0, 'long rest')}>Full heal</button>
        </div>

        <div className="ctrl-row">
          <label htmlFor="tempHp" style={{ color: 'var(--fg-muted)' }}>
            Temp HP
          </label>
          <input
            id="tempHp"
            type="number"
            min={0}
            value={tempHp}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              if (Number.isFinite(v)) sendHpUpdate(undefined, Math.max(0, v));
            }}
            style={{ width: '4rem' }}
          />
        </div>
      </section>

      <section>
        <h2>Hit Dice</h2>
        <div className="ctrl-row" style={{ marginBottom: '0.5rem' }}>
          <span className="numeric" style={{ fontSize: '1.25rem' }}>
            <span style={{ color: hitDiceRemaining === 0 ? 'var(--danger)' : 'var(--fg-base)' }}>
              {hitDiceRemaining}
            </span>
            <span style={{ color: 'var(--fg-muted)' }}> / {maxHitDice}</span>
          </span>
          <span style={{ color: 'var(--fg-muted)', fontSize: '0.85rem' }}>
            remaining
          </span>
        </div>
        <div className="ctrl-row">
          <button onClick={() => adjustHitDice(+1)} disabled={hitDiceRemaining === 0}>
            Spend one
          </button>
          <button onClick={() => adjustHitDice(-1)} disabled={hitDiceSpent === 0}>
            Recover one
          </button>
          <button
            onClick={() => sendHitDiceUpdate(0, 'long rest')}
            disabled={hitDiceSpent === 0}
          >
            Long rest (reset)
          </button>
        </div>
      </section>

      <section>
        <h2>Inspiration</h2>
        {/*
         * Binary flag — you have it or you don't. Rendered as a toggle
         * button rather than a checkbox because the "spent" and "gained"
         * actions feel like distinct deliberate moves at the table, not a
         * fiddly form control.
         */}
        <div className="ctrl-row">
          <span
            style={{
              display: 'inline-block',
              width: '0.9rem',
              height: '0.9rem',
              borderRadius: '50%',
              background: inspiration ? 'var(--sheet-a)' : 'transparent',
              border: '2px solid var(--sheet-a)',
            }}
          />
          <span style={{ color: inspiration ? 'var(--fg-base)' : 'var(--fg-muted)' }}>
            {inspiration ? 'Have inspiration' : 'No inspiration'}
          </span>
          {inspiration ? (
            <button onClick={() => sendInspirationUpdate(false, 'spent')}>
              Spend
            </button>
          ) : (
            <button onClick={() => sendInspirationUpdate(true, 'granted')}>
              Grant
            </button>
          )}
        </div>
      </section>

      <section>
        <h2>Conditions</h2>
        {/*
         * Active-first chip row, then a toggle row of everything else.
         * Exhaustion is pulled out into its own stepper because it carries
         * a 1..6 level the rest of the conditions don't have. Homebrew
         * condition strings aren't wired here yet — the picker only
         * surfaces STANDARD_CONDITIONS.
         */}
        {conditions.length === 0 ? (
          <p style={{ color: 'var(--fg-muted)', fontStyle: 'italic', margin: '0 0 0.5rem' }}>
            None
          </p>
        ) : (
          <div className="chip-row">
            {conditions.map((c) => (
              <span
                key={c.id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  padding: '0.2rem 0.55rem',
                  borderRadius: '999px',
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border)',
                  fontSize: '0.85rem',
                }}
              >
                {c.id}
                {c.level != null && (
                  <span style={{ color: 'var(--fg-muted)' }}>· {c.level}</span>
                )}
                <button
                  onClick={() =>
                    c.id === 'exhaustion'
                      ? setExhaustion(0)
                      : toggleBinary(c.id)
                  }
                  aria-label={`Remove ${c.id}`}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--fg-muted)',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: '1rem',
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="chip-row">
          {STANDARD_CONDITIONS.filter((id) => id !== 'exhaustion').map((id) => {
            const active = hasCondition(id);
            return (
              <button
                key={id}
                onClick={() => toggleBinary(id)}
                style={{
                  opacity: active ? 1 : 0.6,
                  fontWeight: active ? 600 : 400,
                }}
              >
                {active ? '−' : '+'} {id}
              </button>
            );
          })}
        </div>

        <div className="ctrl-row">
          <span style={{ color: 'var(--fg-muted)', fontSize: '0.85rem' }}>Exhaustion</span>
          <button onClick={() => setExhaustion(exhaustionLevel - 1)} disabled={exhaustionLevel === 0}>
            −
          </button>
          <span className="numeric" style={{ minWidth: '1.5rem', textAlign: 'center' }}>
            {exhaustionLevel}
          </span>
          <button onClick={() => setExhaustion(exhaustionLevel + 1)} disabled={exhaustionLevel === 6}>
            +
          </button>
          <span style={{ color: 'var(--fg-muted)', fontSize: '0.85rem' }}>/ 6</span>
        </div>
      </section>

      <section>
        <h2>Class Resources</h2>
        {/*
         * Generic class-feature counters — Ki, Channel Divinity, Sorcery
         * Points, Bardic Inspiration, Lay on Hands, etc. The runtime row
         * only carries spent counts; max lives on the ledger and isn't
         * wired here yet. Until then, the UI shows "spent: N" with +/-
         * and a reset, and relies on the player to know their own cap.
         *
         * Add-resource input lets the player bootstrap a counter by id
         * (e.g. "ki") without the level-up page having seeded it.
         */}
        {resources.length === 0 ? (
          <p style={{ color: 'var(--fg-muted)', fontStyle: 'italic', margin: '0 0 0.5rem' }}>
            No resources tracked yet.
          </p>
        ) : (
          <div style={{ marginBottom: '0.5rem' }}>
            {resources.map((r) => (
              <div key={r.id} className="ctrl-row">
                <span style={{ minWidth: '9rem' }}>{r.id}</span>
                <button onClick={() => adjustResource(r.id, -1)} disabled={r.spent === 0}>
                  −
                </button>
                <span className="numeric" style={{ minWidth: '2rem', textAlign: 'center' }}>
                  {r.spent}
                </span>
                <button onClick={() => adjustResource(r.id, +1)}>+</button>
                <span style={{ color: 'var(--fg-muted)' }}>spent</span>
                <button
                  onClick={() => resetResource(r.id)}
                  disabled={r.spent === 0}
                  style={{ marginLeft: '0.5rem' }}
                >
                  Reset
                </button>
                <button
                  onClick={() => removeResource(r.id)}
                  aria-label={`Remove ${r.id}`}
                  style={{ color: 'var(--fg-muted)' }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            addResource(newResourceId);
            setNewResourceId('');
          }}
          className="ctrl-row"
        >
          <input
            type="text"
            placeholder="ki, channel-divinity, …"
            value={newResourceId}
            onChange={(e) => setNewResourceId(e.target.value)}
            style={{ flex: '0 1 14rem' }}
          />
          <button type="submit" disabled={!newResourceId.trim()}>
            Add counter
          </button>
        </form>
      </section>

      <section>
        <h2>Inventory</h2>
        {inventory.length === 0 ? (
          <p style={{ color: 'var(--fg-muted)', fontStyle: 'italic', margin: '0 0 0.5rem' }}>
            No items yet.
          </p>
        ) : (
          <div style={{ marginBottom: '0.5rem' }}>
            {inventory.map((item) => (
              <div
                key={item.id}
                className="inv-row"
                style={{ opacity: item.equipped ? 1 : 0.7 }}
              >
                <button
                  onClick={() => toggleEquipped(item.id)}
                  title={item.equipped ? 'Equipped — click to stow' : 'Stowed — click to equip'}
                  style={{
                    background: item.equipped ? 'var(--sheet-b)' : 'transparent',
                    border: `2px solid ${item.equipped ? 'var(--sheet-b)' : 'var(--border)'}`,
                    borderRadius: '4px',
                    width: '1.3rem',
                    height: '1.3rem',
                    cursor: 'pointer',
                    padding: 0,
                    flexShrink: 0,
                    minWidth: 'auto',
                    minHeight: 'auto',
                  }}
                  aria-label={item.equipped ? `Stow ${item.name}` : `Equip ${item.name}`}
                />
                <span style={{ minWidth: '7rem', flex: '1 1 0' }}>
                  {item.name}
                </span>
                <button
                  onClick={() => adjustItemQty(item.id, -1)}
                  disabled={(item.quantity ?? 1) <= 1}
                  aria-label={`Decrease ${item.name} quantity`}
                >
                  −
                </button>
                <span className="numeric" style={{ minWidth: '2rem', textAlign: 'center' }}>
                  {item.quantity ?? 1}
                </span>
                <button
                  onClick={() => adjustItemQty(item.id, +1)}
                  aria-label={`Increase ${item.name} quantity`}
                >
                  +
                </button>
                <span style={{ color: 'var(--fg-muted)', fontSize: '0.8rem' }}>
                  {item.equipped ? 'equipped' : 'stowed'}
                </span>
                {item.attuned && (
                  <span style={{ color: 'var(--sheet-a)', fontSize: '0.8rem' }}>
                    attuned
                  </span>
                )}
                <button
                  onClick={() => removeItem(item.id)}
                  aria-label={`Remove ${item.name}`}
                  style={{ color: 'var(--fg-muted)', marginLeft: 'auto' }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            addItem(newItemName);
            setNewItemName('');
          }}
          className="ctrl-row"
        >
          <input
            type="text"
            placeholder="longsword, shield, …"
            value={newItemName}
            onChange={(e) => setNewItemName(e.target.value)}
            style={{ flex: '0 1 14rem' }}
          />
          <button type="submit" disabled={!newItemName.trim()}>
            Add item
          </button>
        </form>
      </section>

      <section>
        <h2>Spell Slots</h2>
        {slotSheetIds.length === 0 && (
          <p style={{ color: 'var(--fg-muted)' }}>
            No slot data yet. Slots populate after the level-up page seeds them.
          </p>
        )}
        {slotSheetIds.map((sid) => {
          const sheet = spellSlots[sid];
          const levels = Object.keys(sheet.leveled)
            .map((k) => parseInt(k, 10))
            .filter((n) => Number.isFinite(n))
            .sort((a, b) => a - b);
          return (
            <div key={sid} style={{ marginBottom: '0.75rem' }}>
              <div style={{ color: 'var(--fg-muted)', marginBottom: '0.25rem' }}>{sid}</div>
              {levels.length === 0 && (
                <div style={{ color: 'var(--fg-muted)', fontStyle: 'italic' }}>
                  no leveled slots
                </div>
              )}
              {levels.map((lvl) => (
                <div key={`${sid}-${lvl}`} className="ctrl-row">
                  <span style={{ width: '5rem' }}>Level {lvl}</span>
                  <button onClick={() => adjustSlot(sid, lvl, -1)}>-</button>
                  <span className="numeric" style={{ minWidth: '2rem', textAlign: 'center' }}>
                    {sheet.leveled[String(lvl)] ?? 0}
                  </span>
                  <button onClick={() => adjustSlot(sid, lvl, +1)}>+</button>
                  <span style={{ color: 'var(--fg-muted)' }}>expended</span>
                </div>
              ))}
              {sheet.pactExpended != null && (
                <div className="ctrl-row" style={{ marginTop: '0.25rem' }}>
                  <span style={{ width: '5rem' }}>Pact</span>
                  <button onClick={() => adjustPact(sid, -1)}>-</button>
                  <span className="numeric" style={{ minWidth: '2rem', textAlign: 'center' }}>
                    {sheet.pactExpended}
                  </span>
                  <button onClick={() => adjustPact(sid, +1)}>+</button>
                  <span style={{ color: 'var(--fg-muted)' }}>expended</span>
                </div>
              )}
            </div>
          );
        })}
      </section>

      <section>
        <h2>Dice Roller</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (diceExpr.trim()) {
              doRoll(diceExpr.trim());
              setDiceExpr('');
            }
          }}
          className="ctrl-row"
        >
          <input
            type="text"
            placeholder="2d6+3, 1d20+5, …"
            value={diceExpr}
            onChange={(e) => setDiceExpr(e.target.value)}
            style={{ flex: '0 1 14rem' }}
          />
          <button type="submit" disabled={!diceExpr.trim()}>
            Roll
          </button>
        </form>

        <div className="chip-row">
          {[20, 4, 6, 8, 10, 12].map((faces) => (
            <button key={`qr-${faces}`} onClick={() => quickRoll(faces)}>
              d{faces}
            </button>
          ))}
        </div>

        {diceError && (
          <p style={{ color: 'var(--danger)', margin: '0.25rem 0' }}>{diceError}</p>
        )}

        {lastRoll && (
          <div
            style={{
              padding: '0.5rem 0.75rem',
              borderRadius: '6px',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              marginBottom: '0.25rem',
            }}
          >
            <div style={{ fontSize: '1.25rem', marginBottom: '0.15rem' }}>
              <span className="numeric">{lastRoll.total}</span>
            </div>
            <div style={{ color: 'var(--fg-muted)', fontSize: '0.85rem' }}>
              {formatRoll(lastRoll)}
            </div>
          </div>
        )}
      </section>

      <section>
        <h2>Session Log</h2>
        {logEntries.length === 0 ? (
          <p style={{ color: 'var(--fg-muted)', fontStyle: 'italic' }}>
            No events yet. Roll some dice!
          </p>
        ) : (
          <div className="session-log">
            {logEntries.map((entry) => (
              <div key={entry.id} className="session-log__entry">
                <span
                  style={{
                    color: entry.kind === 'roll'
                      ? 'var(--sheet-b)'
                      : 'var(--fg-muted)',
                    fontWeight: 600,
                    minWidth: '1.6rem',
                    textAlign: 'center',
                  }}
                  title={entry.kind}
                >
                  {entry.kind === 'roll' ? '\u{1F3B2}' : '\u{1F4DD}'}
                </span>
                <span style={{ color: 'var(--fg-muted)', minWidth: '5rem' }}>
                  {entry.actorUsername}
                </span>
                <span style={{ flex: 1 }}>
                  {entry.summary}
                </span>
                <span style={{ color: 'var(--fg-muted)', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                  {new Date(entry.ts).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      {conflict && (
        <p style={{ color: 'var(--warning)', marginTop: '1rem' }}>{conflict}</p>
      )}
    </div>
  );
}
